// Worm-head cosmetics (shop items, kind "head"): replace the plain orange dot
// with a styled head. Pure-canvas draws shared by the worm-head renderer
// (opponents + offline) and the predicted-worm renderer (your own worm online),
// plus the shop's preview tiles.
//
// drawHeadStyle(ctx, x, y, r, direction, t, style) -> true when it drew
//   x, y     : head position in CANVAS pixels
//   r        : the default head's radius in canvas pixels (draws match its size)
//   direction: worm heading in radians (faces the eyes/flames)
//   t        : seconds (fireball flicker); any monotonic clock works

import { drawEgg } from "../shared/golden-eggs";

import { viking } from "./head-art/crown-designs";
import { gildedShine, jarl } from "./head-art/crown-variations";
export const HEAD_STYLES = ["smiley", "skull", "ghoul", "fireball", "pacman", "pumpkin", "robot", "missile", "rocket", "goldegg", "crown", "viking", "rich-viking"] as const;
export type HeadStyleType = (typeof HEAD_STYLES)[number];

// How much bigger (in game units of radius) a head draws than the body it
// sits on, for cosmetic heads AND the plain default dot/square. A fixed bump
// rather than a multiplier: fat/slim power-ups change the body radius and the
// head follows by that same absolute amount, so it never balloons or shrinks
// with them. Default body radius is 4, so heads draw at 7.
export const HEAD_STYLE_EXTRA = 3;
// Per-style radius nudges on top of HEAD_STYLE_EXTRA, for heads whose artwork
// reads smaller or larger than its nominal radius. Tune by eye in play.
export const HEAD_STYLE_RADIUS_TWEAK: Partial<Record<HeadStyleType, number>> = {
  smiley: 1,
  skull: 1,
  pumpkin: 1,
  ghoul: 1.5,
  fireball: 1.5,
  robot: 1,
  missile: 3,
  rocket: 3,
  pacman: 1,
  goldegg: 2,
  crown: 2,
  viking: 2,
  "rich-viking": 2,
};
export function headStyleRadius(bodyRadius: number, style?: string | null): number {
  const tweak = isHeadStyle(style) ? HEAD_STYLE_RADIUS_TWEAK[style] ?? 0 : 0;
  return bodyRadius + HEAD_STYLE_EXTRA + tweak;
}
// How far a head's artwork reaches from its centre, in head radii: the area
// the renderers wipe around the head each frame. Exhausts and flames trail
// past the 1.8 most heads fit in (the rocket's sparks fly out to about 2.6);
// on a small map, zoomed in, what fell outside the wipe stayed on the board.
const HEAD_STYLE_REACH: Partial<Record<HeadStyleType, number>> = {
  rocket: 2.75,
  missile: 2.35,
  fireball: 2,
  crown: 2,
};
export function headStyleReach(style?: string | null): number {
  return isHeadStyle(style) ? HEAD_STYLE_REACH[style] ?? 1.8 : 1.8;
}

export function isHeadStyle(v: unknown): v is HeadStyleType {
  return typeof v === "string" && (HEAD_STYLES as readonly string[]).indexOf(v) !== -1;
}

const TAU = Math.PI * 2;

// The Golden Egg head is the shop's Hatchling egg (~100 gradient-filled scales
// per frame), far too heavy to redraw per worm. It is rendered once per frame
// per pixel size into a sprite shared by every egg-headed worm on the board.
const EGG_SPRITES = new Map<number, { canvas: HTMLCanvasElement; t: number }>();
function eggSprite(s: number, t: number): HTMLCanvasElement | null {
  const key = Math.round(s);
  let hit = EGG_SPRITES.get(key);
  if (!hit) {
    const canvas = document.createElement("canvas");
    canvas.width = key; canvas.height = key;
    hit = { canvas, t: NaN };
    EGG_SPRITES.set(key, hit);
  }
  if (hit.t !== t) {
    const ctx = hit.canvas.getContext("2d");
    if (!ctx) return null;
    ctx.clearRect(0, 0, key, key);
    drawEgg(ctx, key, t);
    hit.t = t;
  }
  return hit.canvas;
}

// A head that fails to draw (an old browser missing a canvas call) must not
// leave the canvas half-changed: the rest of the frame (other worms, their
// colours) would draw with its leftover state. Reset and report no head.
export function drawHeadStyle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  direction: number,
  t: number,
  style: HeadStyleType,
  // the worm's colour, for the heads that wear it (the Royale crown's bow)
  color: string = "#03A9F4",
): boolean {
  try {
    return drawHeadStyleUnguarded(ctx, x, y, r, direction, t, style, color);
  } catch {
    ctx.restore();
    return false;
  }
}

function drawHeadStyleUnguarded(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  direction: number,
  t: number,
  style: HeadStyleType,
  color: string,
): boolean {
  if (r <= 0) return false;
  ctx.save();
  ctx.translate(x, y);
  switch (style) {
    case "smiley": {
      ctx.fillStyle = "#ffd93b";
      ctx.beginPath(); ctx.arc(0, 0, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = "#b08a00"; ctx.lineWidth = Math.max(0.6, r * 0.12);
      ctx.beginPath(); ctx.arc(0, 0, r, 0, TAU); ctx.stroke();
      ctx.rotate(direction + Math.PI / 2); // face travel direction
      ctx.fillStyle = "#3a2c00";
      ctx.beginPath(); ctx.arc(-r * 0.35, -r * 0.25, r * 0.16, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.arc(r * 0.35, -r * 0.25, r * 0.16, 0, TAU); ctx.fill();
      ctx.strokeStyle = "#3a2c00"; ctx.lineWidth = Math.max(0.7, r * 0.14); ctx.lineCap = "round";
      ctx.beginPath(); ctx.arc(0, r * 0.1, r * 0.45, 0.25 * Math.PI, 0.75 * Math.PI); ctx.stroke();
      break;
    }
    case "skull": {
      ctx.rotate(direction + Math.PI / 2);
      ctx.fillStyle = "#eef1f6";
      // cranium + jaw
      ctx.beginPath(); ctx.arc(0, -r * 0.12, r * 0.95, 0, TAU); ctx.fill();
      ctx.fillRect(-r * 0.55, r * 0.2, r * 1.1, r * 0.75);
      ctx.strokeStyle = "#9aa4b5"; ctx.lineWidth = Math.max(0.5, r * 0.09);
      ctx.beginPath(); ctx.arc(0, -r * 0.12, r * 0.95, 0, TAU); ctx.stroke();
      // sockets + nose + teeth
      ctx.fillStyle = "#1a2030";
      ctx.beginPath(); ctx.arc(-r * 0.38, -r * 0.2, r * 0.26, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.arc(r * 0.38, -r * 0.2, r * 0.26, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.moveTo(0, r * 0.05); ctx.lineTo(-r * 0.12, r * 0.32); ctx.lineTo(r * 0.12, r * 0.32); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#1a2030"; ctx.lineWidth = Math.max(0.4, r * 0.08);
      ctx.beginPath();
      ctx.moveTo(-r * 0.3, r * 0.55); ctx.lineTo(-r * 0.3, r * 0.9);
      ctx.moveTo(0, r * 0.55); ctx.lineTo(0, r * 0.9);
      ctx.moveTo(r * 0.3, r * 0.55); ctx.lineTo(r * 0.3, r * 0.9);
      ctx.stroke();
      break;
    }
    case "fireball": {
      // Comic Blaze (fireball lab pick #5): flat two-tone fire with a bold
      // outline and licks that snap between two frames like a cartoon.
      ctx.rotate(direction);
      const snap = Math.floor(t * 6) % 2;
      ctx.lineJoin = "round";
      ctx.fillStyle = "#ff7828"; ctx.strokeStyle = "#7a2a12"; ctx.lineWidth = Math.max(1, r * 0.16);
      ctx.beginPath();
      ctx.moveTo(r * 1.05, 0);
      ctx.quadraticCurveTo(r * 0.9, -r * 0.85, r * 0.1, -r * 0.9);
      ctx.lineTo(-r * (1.3 + snap * 0.4), -r * 0.55);
      ctx.quadraticCurveTo(-r * 0.7, -r * 0.25, -r * (1.75 - snap * 0.4), 0);
      ctx.lineTo(-r * (1.2 + snap * 0.3), r * 0.45);
      ctx.quadraticCurveTo(-r * 0.5, r * 0.75, r * 0.15, r * 0.88);
      ctx.quadraticCurveTo(r * 0.9, r * 0.8, r * 1.05, 0);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#ffd93b";
      ctx.beginPath();
      ctx.moveTo(r * 0.7, 0);
      ctx.quadraticCurveTo(r * 0.55, -r * 0.5, -r * 0.05, -r * 0.5);
      ctx.lineTo(-r * (0.75 + snap * 0.25), 0);
      ctx.quadraticCurveTo(-r * 0.3, r * 0.42, r * 0.1, r * 0.5);
      ctx.quadraticCurveTo(r * 0.55, r * 0.45, r * 0.7, 0);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.beginPath(); ctx.arc(r * 0.28, -r * 0.12, r * 0.16, 0, TAU); ctx.fill();
      break;
    }
    case "ghoul": {
      // A proper cranium (dome, cheekbones, jaw) whose sockets burn green.
      ctx.rotate(direction + Math.PI / 2);
      ctx.fillStyle = "#dfe5ee"; ctx.strokeStyle = "#8a94a8"; ctx.lineWidth = Math.max(0.5, r * 0.09);
      ctx.beginPath();
      ctx.moveTo(-r * 0.9, -r * 0.05);
      ctx.bezierCurveTo(-r * 0.98, -r * 1.15, r * 0.98, -r * 1.15, r * 0.9, -r * 0.05);
      ctx.bezierCurveTo(r * 0.88, r * 0.22, r * 0.62, r * 0.28, r * 0.55, r * 0.45);
      ctx.lineTo(r * 0.42, r * 0.92);
      ctx.lineTo(-r * 0.42, r * 0.92);
      ctx.lineTo(-r * 0.55, r * 0.45);
      ctx.bezierCurveTo(-r * 0.62, r * 0.28, -r * 0.88, r * 0.22, -r * 0.9, -r * 0.05);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      // crack
      ctx.strokeStyle = "#8a94a8"; ctx.lineWidth = Math.max(0.4, r * 0.06);
      ctx.beginPath(); ctx.moveTo(r * 0.15, -r * 1.02); ctx.lineTo(r * 0.28, -r * 0.7); ctx.lineTo(r * 0.12, -r * 0.5); ctx.stroke();
      // sockets with pulsing green embers
      const g = 0.6 + 0.4 * Math.sin(t * 4.5);
      ctx.fillStyle = "#101820";
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.ellipse(s * r * 0.38, -r * 0.22, r * 0.27, r * 0.32, s * 0.25, 0, TAU); ctx.fill();
      }
      ctx.shadowColor = `rgba(90,255,140,${(0.9 * g).toFixed(3)})`;
      ctx.shadowBlur = r * 0.5;
      ctx.fillStyle = `rgba(120,255,160,${(0.6 + 0.4 * g).toFixed(3)})`;
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.arc(s * r * 0.38, -r * 0.2, r * 0.12, 0, TAU); ctx.fill();
      }
      ctx.shadowBlur = 0;
      // nose cavity + teeth
      ctx.fillStyle = "#101820";
      ctx.beginPath(); ctx.moveTo(0, r * 0.02); ctx.lineTo(-r * 0.13, r * 0.34); ctx.lineTo(r * 0.13, r * 0.34); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#101820"; ctx.lineWidth = Math.max(0.4, r * 0.07);
      for (const k of [-0.28, 0, 0.28]) {
        ctx.beginPath(); ctx.moveTo(k * r, r * 0.5); ctx.lineTo(k * r, r * 0.88); ctx.stroke();
      }
      break;
    }
    case "pacman": {
      ctx.rotate(direction); // mouth opens along travel
      // Arcade-style chomp: closed -> fully open (a 90 degree wedge, i.e. a
      // 45 degree half-angle) -> closed, with linear motion (triangle wave)
      // rather than a sine ease. Cadence matches the original head's rhythm
      // (|sin(5.5t)| = one chomp every ~0.57s).
      const CHOMP_HZ = 1.75;
      const phase = (t * CHOMP_HZ) % 1;
      const m = (Math.PI / 4) * (phase < 0.5 ? phase * 2 : 2 - phase * 2);
      // Opaque dark backing disc: blanks the worm's body under the head so
      // the cut-out mouth opens onto darkness, never onto worm.
      ctx.fillStyle = "#0d1017";
      ctx.beginPath(); ctx.arc(0, 0, r * 1.05, 0, TAU); ctx.fill();
      ctx.fillStyle = "#ffe135";
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, r * 1.05, m, TAU - m); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = "#b89b00"; ctx.lineWidth = Math.max(0.5, r * 0.08);
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, r * 1.05, m, TAU - m); ctx.closePath(); ctx.stroke();
      ctx.fillStyle = "#141414";
      ctx.beginPath(); ctx.arc(r * 0.15, -r * 0.55, r * 0.14, 0, TAU); ctx.fill();
      break;
    }
    case "pumpkin": {
      ctx.save();
      ctx.rotate(direction + Math.PI / 2);
      // lobed pumpkin body: a solid base filling the whole outline (so there is
      // never a dark gap between a lobe's edge and the outline stroke), dark
      // side lobes and a brighter centre lobe on top, every lobe sized to stay
      // INSIDE the near-round outline (a taller side lobe used to poke past it
      // top and bottom, leaving four ears)
      ctx.fillStyle = "#d9660f";
      ctx.beginPath(); ctx.ellipse(0, r * 0.05, r * 1.0, r * 0.96, 0, 0, TAU); ctx.fill();
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.ellipse(s * r * 0.42, r * 0.05, r * 0.5, r * 0.82, 0, 0, TAU); ctx.fill();
      }
      ctx.fillStyle = "#f2801e";
      ctx.beginPath(); ctx.ellipse(0, r * 0.05, r * 0.6, r * 0.92, 0, 0, TAU); ctx.fill();
      ctx.strokeStyle = "#9c4c0a"; ctx.lineWidth = Math.max(0.6, r * 0.1);
      ctx.beginPath(); ctx.ellipse(0, r * 0.05, r * 1.0, r * 0.96, 0, 0, TAU); ctx.stroke();
      // carved face: always black cut-outs (never the worm's colour), with a
      // faint flickering candle glow leaking around the edges
      const glow = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 9) * Math.sin(t * 3.7));
      ctx.shadowColor = `rgba(255,180,60,${(0.6 * glow).toFixed(3)})`;
      ctx.shadowBlur = r * 0.45;
      ctx.fillStyle = "#0a0604";
      for (const s of [-1, 1]) { // triangle eyes
        ctx.beginPath();
        ctx.moveTo(s * r * 0.5, -r * 0.08); ctx.lineTo(s * r * 0.16, -r * 0.08); ctx.lineTo(s * r * 0.33, -r * 0.45);
        ctx.closePath(); ctx.fill();
      }
      ctx.beginPath(); // wide toothy grin: two teeth up, one down
      ctx.moveTo(-r * 0.6, r * 0.28);
      ctx.lineTo(-r * 0.34, r * 0.28); ctx.lineTo(-r * 0.34, r * 0.46); ctx.lineTo(-r * 0.16, r * 0.46);
      ctx.lineTo(-r * 0.16, r * 0.28); ctx.lineTo(r * 0.2, r * 0.28); ctx.lineTo(r * 0.2, r * 0.46);
      ctx.lineTo(r * 0.38, r * 0.46); ctx.lineTo(r * 0.38, r * 0.28); ctx.lineTo(r * 0.6, r * 0.28);
      ctx.lineTo(r * 0.42, r * 0.68); ctx.lineTo(r * 0.1, r * 0.68); ctx.lineTo(r * 0.1, r * 0.52);
      ctx.lineTo(-r * 0.1, r * 0.52); ctx.lineTo(-r * 0.1, r * 0.68); ctx.lineTo(-r * 0.42, r * 0.68);
      ctx.closePath(); ctx.fill();
      ctx.shadowBlur = 0;
      // curved stem + curl, anchored to the pumpkin's crown so it rotates
      // with the head instead of floating in screen space
      ctx.strokeStyle = "#4c7a24"; ctx.lineWidth = Math.max(1, r * 0.2); ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(0, -r * 0.8); ctx.quadraticCurveTo(r * 0.05, -r * 1.3, r * 0.35, -r * 1.4); ctx.stroke();
      ctx.lineWidth = Math.max(0.6, r * 0.09);
      ctx.beginPath(); ctx.arc(r * 0.62, -r * 1.28, r * 0.22, Math.PI * 1.1, Math.PI * 2.4); ctx.stroke();
      ctx.restore();
      break;
    }
    case "robot": {
      ctx.save();
      ctx.rotate(direction + Math.PI / 2);
      // round steel head with rivets
      ctx.fillStyle = "#aeb9cc"; ctx.strokeStyle = "#5e6b7e"; ctx.lineWidth = Math.max(0.6, r * 0.1);
      ctx.beginPath(); ctx.arc(0, 0, r, 0, TAU); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#7e8aa0";
      for (const a of [0.8, 2.35, 3.95, 5.5]) {
        ctx.beginPath(); ctx.arc(Math.cos(a) * r * 0.78, Math.sin(a) * r * 0.78, r * 0.07, 0, TAU); ctx.fill();
      }
      // curved visor band with a scanning LED
      ctx.fillStyle = "#1a2030";
      ctx.beginPath(); ctx.roundRect(-r * 0.72, -r * 0.4, r * 1.44, r * 0.58, r * 0.29); ctx.fill();
      const sx = Math.sin(t * 3.2) * r * 0.5;
      ctx.fillStyle = "#ff4d4d";
      ctx.beginPath(); ctx.arc(sx, -r * 0.11, r * 0.13, 0, TAU); ctx.fill();
      ctx.fillStyle = "rgba(255,77,77,.35)";
      ctx.beginPath(); ctx.arc(sx, -r * 0.11, r * 0.24, 0, TAU); ctx.fill();
      // mouth grille
      ctx.strokeStyle = "#5e6b7e"; ctx.lineWidth = Math.max(0.4, r * 0.07);
      for (const k of [-1, 0, 1]) {
        ctx.beginPath(); ctx.moveTo(k * r * 0.25 - r * 0.08, r * 0.55); ctx.lineTo(k * r * 0.25 + r * 0.08, r * 0.55); ctx.stroke();
      }
      // antenna: anchored to the crown of the head, rotating WITH it (drawn
      // in screen space it visibly detached whenever the head turned)
      ctx.strokeStyle = "#5e6b7e"; ctx.lineWidth = Math.max(0.7, r * 0.1);
      ctx.beginPath(); ctx.moveTo(0, -r * 0.95); ctx.lineTo(0, -r * 1.45); ctx.stroke();
      ctx.fillStyle = Math.sin(t * 5) > 0 ? "#ff4d4d" : "#7a2a2a";
      ctx.beginPath(); ctx.arc(0, -r * 1.55, r * 0.16, 0, TAU); ctx.fill();
      ctx.restore();
      break;
    }
    case "missile": {
      ctx.rotate(direction); // nose points along travel
      // Sits a quarter head-radius forward of the head centre (picked on the
      // ship-offset board against the collision circle).
      ctx.translate(r * 0.25, 0);
      // exhaust flames (behind, flickering)
      const flick = (k: number) => 0.7 + 0.3 * Math.sin(t * 15 + k * 2.3);
      for (let k = 0; k < 3; k++) {
        const fy = (k - 1) * r * 0.28;
        const fl = r * (0.9 + 0.7 * flick(k));
        ctx.fillStyle = k === 1 ? "rgba(255,214,80,.95)" : "rgba(255,120,40,.85)";
        ctx.beginPath();
        ctx.moveTo(-r * 0.85, fy - r * 0.18);
        ctx.quadraticCurveTo(-r * 0.85 - fl, fy, -r * 0.85, fy + r * 0.18);
        ctx.closePath(); ctx.fill();
      }
      // tail fins
      ctx.fillStyle = "#7e8aa0";
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(-r * 0.85, s * r * 0.3);
        ctx.lineTo(-r * 1.15, s * r * 0.85);
        ctx.lineTo(-r * 0.45, s * r * 0.42);
        ctx.closePath(); ctx.fill();
      }
      // body + red nosecone + warning stripe
      ctx.fillStyle = "#aeb9cc"; ctx.strokeStyle = "#5e6b7e"; ctx.lineWidth = Math.max(0.5, r * 0.09);
      ctx.beginPath(); ctx.roundRect(-r * 0.9, -r * 0.45, r * 1.3, r * 0.9, r * 0.2); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e04343";
      ctx.beginPath();
      ctx.moveTo(r * 0.35, -r * 0.45);
      ctx.quadraticCurveTo(r * 1.25, 0, r * 0.35, r * 0.45);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#ffcf4d";
      ctx.fillRect(-r * 0.15, -r * 0.45, r * 0.22, r * 0.9);
      break;
    }
    case "rocket": {
      ctx.rotate(direction);
      // Drawn centred on the head (offset 0, picked on the ship-offset board
      // against the collision circle); its flame is kept short so it doesn't
      // drag the eye behind the head.
      // exhaust: pulsing teardrop + sparks
      const fl = r * (0.85 + 0.4 * Math.sin(t * 12));
      ctx.fillStyle = "rgba(255,150,50,.9)";
      ctx.beginPath();
      ctx.moveTo(-r * 0.8, -r * 0.28);
      ctx.quadraticCurveTo(-r * 0.8 - fl, 0, -r * 0.8, r * 0.28);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "rgba(255,230,120,.95)";
      ctx.beginPath();
      ctx.moveTo(-r * 0.8, -r * 0.14);
      ctx.quadraticCurveTo(-r * 0.8 - fl * 0.55, 0, -r * 0.8, r * 0.14);
      ctx.closePath(); ctx.fill();
      for (let k = 0; k < 3; k++) {
        const u = (t * 2.2 + k * 0.33) % 1;
        ctx.fillStyle = `rgba(255,190,90,${(1 - u).toFixed(3)})`;
        ctx.beginPath(); ctx.arc(-r * 0.9 - u * r * 1.6, Math.sin(k * 4 + t * 3) * r * 0.3, r * 0.09, 0, TAU); ctx.fill();
      }
      // fins
      ctx.fillStyle = "#e04343"; ctx.strokeStyle = "#8f1d1d"; ctx.lineWidth = Math.max(0.4, r * 0.07);
      for (const s of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(-r * 0.55, s * r * 0.35);
        ctx.quadraticCurveTo(-r * 1.05, s * r * 0.95, -r * 0.75, s * r * 0.95);
        ctx.lineTo(-r * 0.15, s * r * 0.5);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      // white body, red nose, porthole
      ctx.fillStyle = "#f2f5fa"; ctx.strokeStyle = "#9aa4b5"; ctx.lineWidth = Math.max(0.5, r * 0.09);
      ctx.beginPath();
      ctx.moveTo(-r * 0.8, -r * 0.42);
      ctx.quadraticCurveTo(r * 0.35, -r * 0.62, r * 1.15, 0);
      ctx.quadraticCurveTo(r * 0.35, r * 0.62, -r * 0.8, r * 0.42);
      ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "#e04343";
      ctx.beginPath();
      ctx.moveTo(r * 0.55, -r * 0.34);
      ctx.quadraticCurveTo(r * 1.15, 0, r * 0.55, r * 0.34);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#28c4d4"; ctx.strokeStyle = "#9aa4b5";
      ctx.beginPath(); ctx.arc(-r * 0.05, 0, r * 0.26, 0, TAU); ctx.fill(); ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,.7)";
      ctx.beginPath(); ctx.arc(-r * 0.12, -r * 0.08, r * 0.08, 0, TAU); ctx.fill();
      break;
    }
    case "goldegg": {
      // egg square of side s: the shell spans 64% of s across and 84% tall,
      // so the egg sits ~1.9r wide and ~2.4r long with its tip leading
      const s = r * 2.9;
      const sprite = eggSprite(s, t);
      if (!sprite) { ctx.restore(); return false; }
      ctx.rotate(direction + Math.PI / 2);
      ctx.drawImage(sprite, -s / 2, -s * 0.52, s, s);
      break;
    }
    case "crown": {
      // The Royale's crown (not sold: it comes with a first Royale win): a gilded
      // laurel with glints flashing round it, a bow in the worm's colour
      // (head-art/crown-variations.ts, picked in the crown lab).
      gildedShine(ctx, r, direction, t, color);
      break;
    }
    case "viking": {
      // A steel helm with a gold band and nose ridge, ivory horns (head-art/crown-designs.ts).
      viking(ctx, r, direction, t, color);
      break;
    }
    case "rich-viking": {
      // The jarl's helm: gold trim and crest, tall gold-capped horns (head-art/crown-variations.ts).
      jarl(ctx, r, direction, t, color);
      break;
    }
    default:
      ctx.restore();
      return false;
  }
  ctx.restore();
  return true;
}
